import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";

/**
 * The gate for STARTING a signing: platform admins always, otherwise the org's
 * `signing_enabled` rollout flag AND the `ALLOW_SIGNING` role permission.
 * Extracted so the documents bulk bar and the document list table can't drift
 * apart on who may sign.
 *
 * Deliberately NOT the gate for the Signing page, the signer picker, or
 * finishing a chain — see {@link useSigningVisible}. This one is about who may
 * START a signing; that one is about who may SEE the chained surfaces, and an
 * invited signer needs the second without the first.
 */
export function useSigningAccess(): boolean {
  const { currentOrganization } = useOrganizationContext();
  const { hasPermission, isPlatformAdmin } = usePermissions();

  return (
    isPlatformAdmin ||
    (currentOrganization?.signing_enabled === true && hasPermission(PERMISSIONS.ALLOW_SIGNING))
  );
}

/**
 * The gate for the two CHAINED-SIGNING surfaces — the Signing page (plus its
 * tab) and the "send to others to sign" picker in the sign dialog.
 *
 * **Released to every organisation** (2026-08-14). It read `isPlatformAdmin`
 * alone between 2026-08-06 and this change, while add/remove-signer-mid-chain
 * and resend-invitation were unbuilt; both are still unbuilt, and the decision
 * is that neither blocks the flow customers do have — start a chain, sign your
 * turn, watch it finish, cancel your own. Franklin staff keep their bypass so
 * they can support a chain in an org they are not a member of.
 *
 * The org flag is the only kill switch, so switching a single org back off is a
 * one-row UPDATE rather than a deploy. Today it is on everywhere: the column
 * defaults true and every existing row was set true (franklin-data-pipeline
 * migration `20260629114248_enable_signing_for_all_orgs`), so in practice this
 * reads "everyone" until somebody turns one off.
 *
 * **Off does not reach in-flight chains, and must not strand them.** Nothing
 * server-side reads the flag once a chain exists: `dokobit-sign-open` never
 * checks it (design §4.5), `dokobit-sign-cancel` asks only sender-or-platform-
 * admin, and the hourly `dokobit-sign-sweep` keeps reconciling and mailing turn
 * letters — whose link is the Signing page itself. So this hook returning false
 * closes the SURFACES (tab, page, signer picker) and, with
 * {@link useSigningAccess}, the start of anything new; it never closes a chain.
 * An invited signer would lose the only screen that offers their turn and the
 * sender the only Cancel button, leaving the chain to run to expiry with the
 * reminders still going out. Hence {@link shouldLeaveSigningPage}: the page
 * itself falls back to "do you have rows here", so a switched-off org keeps its
 * open chains finishable and cancellable by direct link while everyone with
 * nothing in flight is still bounced.
 *
 * `ALLOW_SIGNING` must NOT gate this — the reason survives the release intact.
 * Chaining means somebody can be invited to sign without ever being allowed to
 * start one, and `dokobit-sign-open` deliberately does not re-check the
 * permission for exactly that reason (design §4.5, §7): gating on it would
 * build chains that can never complete, because the invited signer could not
 * reach the surface that offers their turn. That is also why this is a separate
 * hook from {@link useSigningAccess} rather than one gate with an extra clause.
 *
 * It was never the data boundary and still isn't. Every listed chain is scoped
 * by RLS — `_signing_session_visible` returns the ones you sent, were invited
 * to, or can see through VIEW_ALL_ORG_BUILDINGS / VIEW_ASSIGNED_BUILDINGS /
 * VIEW_ASSIGNED_PROJECTS — and every write is service-role behind the edge
 * functions, which re-check `signing_enabled` + `ALLOW_SIGNING` themselves
 * (`dokobit-sign-start`) and refuse a cancel that is not yours
 * (`dokobit-sign-cancel`). Flipping this hook changes what is on screen, never
 * what is reachable.
 */
export function useSigningVisible(): boolean {
  const { currentOrganization } = useOrganizationContext();
  const { isPlatformAdmin } = usePermissions();

  return isPlatformAdmin || currentOrganization?.signing_enabled === true;
}

/**
 * Whether the Signing page must bounce this viewer — {@link useSigningVisible}
 * for the org, with the switched-off org's escape hatch: rows the viewer can
 * already see keep the page open.
 *
 * The list is RLS-scoped to chains you sent, were invited to, or manage
 * (`_signing_session_visible`) plus the project's finished containers, so
 * "has rows" is precisely the set of people an off switch would otherwise
 * strand — no client-side ownership check to keep in sync with the policy.
 * Still loading counts as staying: a bounce decided before the queries answer
 * would bounce everyone.
 *
 * The sidebar tab does NOT get this fallback (it would cost every building page
 * a signing query to draw a tab). The turn letter links to the page directly,
 * which is how a signer in a switched-off org arrives.
 */
export function shouldLeaveSigningPage(args: {
  visible: boolean;
  isLoading: boolean;
  itemCount: number;
}): boolean {
  return !args.visible && !args.isLoading && args.itemCount === 0;
}
